import { ChevronDown, MapPin, Check, Building2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

// Re-export from centralized context for backward compatibility
export { useBranch, BranchProvider, useBranchHeaders } from '@/context/BranchContext';
import { useBranch } from '@/context/BranchContext';

// Branch Switcher Dropdown Component - For global header use
export default function BranchSwitcher({ compact = false, variant = 'default' }) {
  const { branches, currentBranch, loading, switchBranch, hasMutipleBranches } = useBranch();

  if (loading) {
    return (
      <Button variant="ghost" size="sm" disabled className="gap-2">
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span className="hidden sm:inline">Loading...</span>
      </Button>
    );
  }

  // Only show if there are multiple branches
  if (!hasMutipleBranches) {
    if (compact) return null;
    return currentBranch ? (
      <div className="flex items-center gap-2 text-sm text-muted-foreground px-2">
        <Building2 className="w-4 h-4" />
        <span>{currentBranch.name}</span>
      </div>
    ) : null;
  }

  // Header variant for global header usage - darker styling
  const isHeaderVariant = variant === 'header';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={isHeaderVariant ? "ghost" : "outline"} 
          size="sm" 
          className={`gap-2 min-w-[140px] justify-between ${
            isHeaderVariant 
              ? 'text-gray-700 dark:text-[#d6deeb] hover:bg-gray-100 dark:hover:bg-[#1d3b53] border border-gray-200 dark:border-[#1d3b53]' 
              : ''
          }`}
          data-testid="branch-switcher"
        >
          <div className="flex items-center gap-2">
            <Building2 className={`w-4 h-4 ${isHeaderVariant ? 'text-blue-500 dark:text-[#82aaff]' : 'text-muted-foreground'}`} />
            <span className="truncate max-w-[100px]">
              {currentBranch?.name || 'Select Branch'}
            </span>
          </div>
          <ChevronDown className="w-4 h-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[220px] dark:bg-[#0b2942] dark:border-[#1d3b53]">
        <DropdownMenuLabel className="flex items-center gap-2 dark:text-[#d6deeb]">
          <MapPin className="w-4 h-4" />
          Switch Branch
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="dark:bg-[#1d3b53]" />
        {branches.map(branch => (
          <DropdownMenuItem
            key={branch.id}
            onClick={() => {
              switchBranch(branch);
              toast.success(`Switched to ${branch.name}`);
            }}
            className="flex items-center justify-between cursor-pointer dark:text-[#d6deeb] dark:hover:bg-[#1d3b53] dark:focus:bg-[#1d3b53]"
            data-testid={`branch-option-${branch.id}`}
          >
            <div className="flex flex-col">
              <span className="font-medium">{branch.name}</span>
              {branch.city && (
                <span className="text-xs text-muted-foreground dark:text-[#637777]">
                  {branch.city}{branch.state ? `, ${branch.state}` : ''}
                </span>
              )}
            </div>
            {currentBranch?.id === branch.id && (
              <Check className="w-4 h-4 text-green-600 dark:text-[#22da6e]" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
